import { useState, useRef } from 'react'
import { PDFDocument } from 'pdf-lib'
import { Upload, Download, FileText } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function PDFSplitter() {
  const [file, setFile] = useState(null)
  const [splitMode, setSplitMode] = useState('all')
  const [pageRange, setPageRange] = useState('')
  const [splitPdfs, setSplitPdfs] = useState([])
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile)
      setSplitPdfs([])
    } else if (selectedFile) {
      alert('Please select a PDF file')
    }
  }

  const splitPDF = async () => {
    if (!file) {
      alert('Please select a PDF file')
      return
    }

    setProcessing(true)
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdfDoc = await PDFDocument.load(arrayBuffer)
      const totalPages = pdfDoc.getPageCount()
      const results = []

      if (splitMode === 'all') {
        // Split each page into separate PDF
        for (let i = 0; i < totalPages; i++) {
          const newPdf = await PDFDocument.create()
          const [page] = await newPdf.copyPages(pdfDoc, [i])
          newPdf.addPage(page)
          const pdfBytes = await newPdf.save()
          results.push({
            page: i + 1,
            blob: new Blob([pdfBytes], { type: 'application/pdf' }),
            url: URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }))
          })
        }
      } else if (splitMode === 'range') {
        // Split by page range
        const ranges = pageRange.split(',').map(r => r.trim())
        for (let range of ranges) {
          if (range.includes('-')) {
            const [start, end] = range.split('-').map(n => parseInt(n.trim()) - 1)
            if (start >= 0 && end < totalPages && start <= end) {
              const newPdf = await PDFDocument.create()
              const pages = await newPdf.copyPages(pdfDoc, Array.from({ length: end - start + 1 }, (_, i) => start + i))
              pages.forEach(page => newPdf.addPage(page))
              const pdfBytes = await newPdf.save()
              results.push({
                range: `${start + 1}-${end + 1}`,
                blob: new Blob([pdfBytes], { type: 'application/pdf' }),
                url: URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }))
              })
            }
          } else {
            const pageNum = parseInt(range) - 1
            if (pageNum >= 0 && pageNum < totalPages) {
              const newPdf = await PDFDocument.create()
              const [page] = await newPdf.copyPages(pdfDoc, [pageNum])
              newPdf.addPage(page)
              const pdfBytes = await newPdf.save()
              results.push({
                page: pageNum + 1,
                blob: new Blob([pdfBytes], { type: 'application/pdf' }),
                url: URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }))
              })
            }
          }
        }
      }

      setSplitPdfs(results)
    } catch (error) {
      alert('Error splitting PDF: ' + error.message)
    } finally {
      setProcessing(false)
    }
  }

  const downloadSplit = (index) => {
    const split = splitPdfs[index]
    const link = document.createElement('a')
    link.href = split.url
    link.download = `split-${split.page || split.range || index + 1}.pdf`
    link.click()
  }

  const downloadAll = () => {
    splitPdfs.forEach((split, index) => {
      setTimeout(() => {
        downloadSplit(index)
      }, index * 200)
    })
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Select PDF File:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="pdf-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <Upload className="w-5 h-5 mr-2" />
              {file ? 'Change PDF' : 'Upload PDF'}
            </Button>
            {file && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-gray-900">{file.name}</span>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Split Mode:</label>
            <select
              value={splitMode}
              onChange={(e) => setSplitMode(e.target.value)}
              className="input w-full"
            >
              <option value="all">Split into individual pages</option>
              <option value="range">Split by page range</option>
            </select>
          </div>

          {splitMode === 'range' && (
            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-3">Page Range:</label>
              <input
                type="text"
                value={pageRange}
                onChange={(e) => setPageRange(e.target.value)}
                className="input w-full"
                placeholder="e.g., 1-3, 5, 7-10"
              />
              <p className="text-xs text-gray-500 mt-2">
                Enter page numbers or ranges separated by commas (e.g., 1-3, 5, 7-10)
              </p>
            </div>
          )}

          <Button
            onClick={splitPDF}
            variant="primary"
            className="w-full"
            disabled={!file || processing}
          >
            {processing ? 'Splitting PDF...' : 'Split PDF'}
          </Button>
        </Card.Body>
      </Card>

      {splitPdfs.length > 0 && (
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Split Results ({splitPdfs.length} files):
              </h3>
              <Button onClick={downloadAll} variant="outline" size="sm">
                Download All
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {splitPdfs.map((split, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {split.page ? `Page ${split.page}` : `Range ${split.range}`}
                    </span>
                    <Button
                      onClick={() => downloadSplit(index)}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {!file && (
        <Card>
          <Card.Body className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Upload a PDF file to split it</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

